import EditIcon from "@mui/icons-material/Edit";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePermission from "hooks/usePermission";
import { useEffect, useMemo, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportTermsAndConditionCSV,
  fetchTermsAndConditions as OnfetchTermsAndConditions,
  updateStatusInTermsAndCondition,
} from "slices/Masters/terms-and-condition/thunk";
import TruncatedTextWithTooltip from "Common/Components/TruncatedTextWithTooltip/TruncatedTextWithTooltip";

const TermsAndCondition = () => {
  document.title = "Terms And Conditions List";
  const dispatch = useDispatch<any>();

  const selectTermsAndConditions = createSelector(
    (state: any) => state.TermsAndCondition,
    (termsAndCondition) => ({
      termsAndConditions: termsAndCondition.termsAndConditionsList,
      rowCount: termsAndCondition.rowCount,
      loading: termsAndCondition.loading,
      error: termsAndCondition.error,
    })
  );

  const { termsAndConditions, rowCount, loading, error } = useSelector(
    selectTermsAndConditions
  );

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.terms_and_conditions
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const [tableInstance, setTableInstance] = useState<any>();
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.TERMS_AND_CONDITIONS,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(
      OnfetchTermsAndConditions({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    setData(termsAndConditions || []);
    setTotalRecords(rowCount);
  }, [termsAndConditions, rowCount]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);



  const handleStatusToggle = async (termsAndCondition: any) => {
    const updatedStatus = !termsAndCondition.is_active;
    const updatedTermsAndCondition = {
      id: termsAndCondition.terms_and_condition_id || termsAndCondition.id,
      is_active: updatedStatus,
    };
    await dispatch(updateStatusInTermsAndCondition(updatedTermsAndCondition))
      .unwrap()
      .then(() => {
        toast.success(
          `Terms & Condition ${updatedStatus ? "activated" : "deactivated"
          } successfully`
        );

        dispatch(
          OnfetchTermsAndConditions({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );
      });
  };

  // Mapping from backend enum values to UI display values
  const enumToTypeMap: Record<string, string> = {
    AFTER_SALES_SERVICE: "After Sales Service",
    DELIVERY: "Delivery",
    GUARANTEE: "Guarantee",
    PRICE: "Price",
    PAYMENT: "Payment",
    SCOPE_OF_SUPPLY: "Scope of Supply",
    VALIDITY: "Validity",
    OTHER: "Other",
  };

  const getTypeBadgeVariant = (typeName: string) => {
    const type = typeName?.toLowerCase() || "";
    if (type.includes("price")) return "success";
    if (type.includes("payment")) return "warning";
    if (type.includes("delivery")) return "info";
    if (type.includes("validity")) return "danger";
    if (type.includes("guarantee")) return "primary";
    return "secondary";
  };

  const columns = [
    {
      header: "Sr. No.",
      accessorKey: "sr_no",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Type",
      accessorKey: "type",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.FILTER_DROPDOWN("type"),
      cell: ({ row }: { row: any }) => {
        const backendType = row.original.type_name || row.original.type || "";
        // Convert backend enum value to UI display value
        const displayType = enumToTypeMap[backendType] || backendType;
        const variant = getTypeBadgeVariant(displayType);
        return (
          <Badge bg={variant} className="px-2 py-1">
            {displayType}
          </Badge>
        );
      },
    },
    {
      header: "Options",
      accessorKey: "options",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.FILTER_DROPDOWN("options"),
      cell: ({ row }: { row: any }) => (
        <TruncatedTextWithTooltip
          text={row.original.options || row.original.term_text || "-"}
          minWidth="200px"
          maxWidth="200px"
          maxLines={3}
        />
      ),
    },
    {
      header: "Status",
      accessorKey: "is_active",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const pkg = row.original;

        return (
          <div
            style={{
              fontSize: "1.5rem",
              cursor: canUpdate ? "pointer" : "not-allowed",
            }}
            onClick={() => canUpdate && handleStatusToggle(pkg)}
          >
            <i
              className={`bi ${pkg.is_active
                  ? "bi-toggle-on text-success"
                  : "bi-toggle-off text-danger"
                }`}
            />
          </div>
        );
      },
    },
    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      filterFetchUrl:
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.FILTER_DROPDOWN(
          "created_at"
        ),
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.created_by_user?.username}
          date={row.original.created_at}
        />
      ),
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      filterFetchUrl:
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.FILTER_DROPDOWN(
          "updated_at"
        ),
      cell: ({ row }: { row: any }) => (
        <NameWithDate
          name={row.original.updated_by_user?.username}
          date={row.original.updated_at}
        />
      ),
    },
    // Filter menu items to check if any will be visible
    // This ensures we only show the Action column if there are visible menu items
    ...(canUpdate
      ? [
        {
          header: "Action",
          accessorKey: "action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const menuItems = [
              {
                icon: EditIcon,
                label: "Edit",
                permission: canUpdate,
                link: `/master/edit-terms-and-condition/${row.original.terms_and_condition_id || row.original.id
                  }`,
                color: "primary",
              },
            ].filter((item) => item.permission === true);
            return (
              <DynamicMenuItem row={row.original} menuItems={menuItems} />
            );
          },
        },
      ]
      : []),
  ]

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <ListingPageContainer>
      <Row id="termsAndConditionList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Terms & Conditions</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportButton
                            onExport={async () => {
                              return await exportTermsAndConditionCSV({
                                search,
                                filters: filterValue,
                              });
                            }}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add Terms & Condition"
                            icon="bi bi-plus-circle"
                            navigateLink="/master/add-terms-and-condition"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper
                loading={loading || isStatusUpdateLoading}
                error={error}
              >
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default TermsAndCondition;
